import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

/**
 * MultiProfile context (Phase 4 — Active Profile selector).
 *
 * Tracks all profiles owned by the logged-in user and exposes an
 * "active profile" stored in localStorage. The active profile is the
 * one currently being managed (its catalog/calendar/stats apply).
 *
 * Refetches on login/logout. Defaults the active profile to the first
 * one in the list if nothing is stored or the stored id is stale.
 */
const STORAGE_KEY = "red.activeProfileId";
const MultiProfileContext = createContext(null);

export const MultiProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [loading, setLoading] = useState(false);
  // Track whether we've ever observed a fully-authenticated user. Only when
  // the user transitions from authed → false (real logout) do we wipe the
  // stored active profile. The initial AuthContext value is `null` while the
  // session probe runs; we must NOT clear localStorage during that probe.
  const wasAuthedRef = useRef(false);

  const load = useCallback(async () => {
    if (!user) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/profiles/me");
      const list = data.items || [];
      setItems(list);
      // Ensure activeId points to a real, owned profile
      const stored = localStorage.getItem(STORAGE_KEY);
      const ok = list.some((p) => p.id === stored);
      if (!ok) {
        const fallback = list[0]?.id || "";
        localStorage.setItem(STORAGE_KEY, fallback);
        setActiveIdState(fallback);
      }
    } catch (err) {
      // Silent — user may be a client/admin without profiles
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      wasAuthedRef.current = true;
      load();
    } else if (user === false && wasAuthedRef.current) {
      // Real logout: user was authed before, now signed out → clear everything
      setItems([]);
      setActiveIdState("");
      localStorage.removeItem(STORAGE_KEY);
      wasAuthedRef.current = false;
    }
    // user === null  → AuthContext still hydrating; keep localStorage intact
  }, [user, load]);

  const setActiveId = (pid) => {
    localStorage.setItem(STORAGE_KEY, pid || "");
    setActiveIdState(pid || "");
  };

  const active = items.find((p) => p.id === activeId) || items[0] || null;

  return (
    <MultiProfileContext.Provider value={{ items, active, activeId: active?.id || "", loading, setActiveId, refresh: load }}>
      {children}
    </MultiProfileContext.Provider>
  );
};

export const useMultiProfile = () => useContext(MultiProfileContext) || { items: [], active: null, activeId: "", loading: false, setActiveId: () => {}, refresh: () => {} };
